import { useCallback, useEffect, useState } from "react";
import { fetchHost, postHost } from "./api.js";
import { MeetAvatar } from "./VideoTile.jsx";

function shortId(id) {
  const s = String(id || "");
  if (s.length < 16) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function CopyAddr({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span>—</span>;
  return (
    <span title={value} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span>{shortId(value)}</span>
      <button
        type="button"
        className="bm-copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 900);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? "copied" : label || "copy"}
      </button>
    </span>
  );
}

export function HostConsole({ apiBase, rooms, roomId: initialRoomId, canOperate, onClose }) {
  const [tab, setTab] = useState("attendance");
  const [roomId, setRoomId] = useState(initialRoomId || (!canOperate && rooms[0]?._id) || "");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [denyId, setDenyId] = useState("");
  const [denyReason, setDenyReason] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      if (tab === "attendance") {
        const q = roomId ? `?roomId=${encodeURIComponent(roomId)}` : "";
        const d = await fetchHost(apiBase, `/api/host/attendance${q}`);
        setRows(d.rows || []);
      } else if (tab === "people") {
        const d = await fetchHost(apiBase, "/api/host/profiles");
        setRows(d.rows || []);
      } else if (tab === "signins") {
        const d = await fetchHost(apiBase, "/api/host/auth");
        setRows(d.rows || []);
      } else {
        const d = await fetchHost(apiBase, "/api/host/denials");
        setRows(d.rows || []);
      }
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setBusy(false);
    }
  }, [apiBase, tab, roomId]);

  useEffect(() => {
    load();
  }, [load]);

  const deny = async (e) => {
    e.preventDefault();
    if (!denyId.trim()) return;
    setBusy(true);
    setError("");
    try {
      await postHost(apiBase, "/api/host/deny", { userid: denyId.trim(), reason: denyReason.trim() });
      setDenyId("");
      setDenyReason("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Deny failed");
      setBusy(false);
    }
  };

  const allow = async (userid) => {
    setBusy(true);
    setError("");
    try {
      await postHost(apiBase, "/api/host/allow", { userid });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Allow failed");
      setBusy(false);
    }
  };

  return (
    <div className="bm-host">
      <div className="bm-host-head">
        <div>
          <h2>Host console</h2>
          <p>Attendance, profiles, and Carrier addresses so you can message people. We never hold private keys or passwords.</p>
        </div>
        <button type="button" className="bm-join sec" onClick={onClose}>
          Back to rooms
        </button>
      </div>
      <div className="bm-host-tabs">
        <button type="button" className={`bm-chip ${tab === "attendance" ? "on" : ""}`} onClick={() => setTab("attendance")}>
          Attendance
        </button>
        {canOperate ? (
          <>
            <button type="button" className={`bm-chip ${tab === "people" ? "on" : ""}`} onClick={() => setTab("people")}>
              People
            </button>
            <button type="button" className={`bm-chip ${tab === "signins" ? "on" : ""}`} onClick={() => setTab("signins")}>
              Sign-ins
            </button>
            <button type="button" className={`bm-chip ${tab === "access" ? "on" : ""}`} onClick={() => setTab("access")}>
              Access
            </button>
          </>
        ) : null}
        {tab === "attendance" ? (
          <select className="bm-host-select" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            {canOperate ? <option value="">All rooms</option> : null}
            {rooms.map((r) => (
              <option key={r._id} value={r._id}>
                {r.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {error ? <div className="bm-err">{error}</div> : null}
      {tab === "access" ? (
        <form className="bm-host-form" onSubmit={deny}>
          <div className="bm-search">
            <input value={denyId} onChange={(e) => setDenyId(e.target.value)} placeholder="Userid to deny" />
          </div>
          <div className="bm-search">
            <input value={denyReason} onChange={(e) => setDenyReason(e.target.value)} placeholder="Reason (optional)" />
          </div>
          <button type="submit" className="bm-join" disabled={busy || !denyId.trim()}>
            Deny
          </button>
        </form>
      ) : null}
      <div className="bm-host-table-wrap">
        {busy && rows.length === 0 ? <div className="bm-empty">Loading…</div> : null}
        {!busy && rows.length === 0 ? <div className="bm-empty">No rows</div> : null}
        {tab === "attendance" && rows.length > 0 ? (
          <table className="bm-host-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Room</th>
                <th>Name</th>
                <th>Carrier address</th>
                <th>Client</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{when(r.at)}</td>
                  <td>{r.type}</td>
                  <td title={r.roomId}>{shortId(r.roomId || r.room_id)}</td>
                  <td title={r.userid}>{r.name || shortId(r.userid)}</td>
                  <td>
                    <CopyAddr value={r.address} />
                  </td>
                  <td>
                    {r.client || ""} {r.platform || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === "people" && rows.length > 0 ? (
          <table className="bm-host-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Carrier address</th>
                <th>Userid</th>
                <th>Punk</th>
                <th>Last seen</th>
                <th>Via</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userid}>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <MeetAvatar identity={{ name: r.name, userid: r.userid, punkId: r.punkId, avatarUrl: r.avatarUrl }} size={28} />
                      {r.name || "—"}
                    </span>
                  </td>
                  <td>
                    <CopyAddr value={r.address} />
                  </td>
                  <td title={r.decentUserid || r.userid}>{shortId(r.decentUserid || r.userid)}</td>
                  <td>{r.punkId ?? "—"}</td>
                  <td>
                    {when(r.lastSeen?.at)} {r.lastSeen?.client || ""}
                  </td>
                  <td>{r.via || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === "signins" && rows.length > 0 ? (
          <table className="bm-host-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Name</th>
                <th>Carrier address</th>
                <th>Source</th>
                <th>Client</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{when(r.at)}</td>
                  <td title={r.userid}>{r.name || shortId(r.userid)}</td>
                  <td>
                    <CopyAddr value={r.address} />
                  </td>
                  <td>{r.source || ""}</td>
                  <td>
                    {r.client || ""} {r.platform || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === "access" && rows.length > 0 ? (
          <table className="bm-host-table">
            <thead>
              <tr>
                <th>Userid</th>
                <th>Reason</th>
                <th>By</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userid}>
                  <td title={r.userid}>{shortId(r.userid)}</td>
                  <td>{r.reason || "—"}</td>
                  <td title={r.byUserid}>{shortId(r.byUserid)}</td>
                  <td>{when(r.at)}</td>
                  <td>
                    <button type="button" className="bm-copy" onClick={() => allow(r.userid)}>
                      Allow
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}
